import React, { useState } from 'react';
import { X, Copy, Download, FileText, Check, Maximize2, Minimize2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface ArtifactPanelProps {
  content: string;
  title?: string;
  onClose: () => void;
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
}

export function ArtifactPanel({ 
  content, 
  title = "Report", 
  onClose,
  isFullscreen = false,
  onToggleFullscreen
}: ArtifactPanelProps) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadMd = () => {
    setDownloading(true);
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = title.replace(/[^a-z0-9]/gi, '_').slice(0, 50);
    a.download = `${safeTitle}_${new Date().toISOString().slice(0, 10)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setTimeout(() => setDownloading(false), 1000);
  };

  return (
    <div className={`flex flex-col bg-white border-l border-gray-200 ${isFullscreen ? 'fixed inset-0 z-50' : 'h-full'}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-gray-500" />
          <span className="font-medium text-sm text-gray-700 truncate max-w-[200px]">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleCopy}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title="Copy to clipboard"
          >
            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
          </button>
          <button
            onClick={handleDownloadMd}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            title="Download as Markdown"
            disabled={downloading}
          >
            <Download className={`h-4 w-4 ${downloading ? 'animate-pulse' : ''}`} />
          </button>
          {onToggleFullscreen && (
            <button
              onClick={onToggleFullscreen}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors ml-1"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <article className="prose prose-sm max-w-none prose-headings:font-semibold prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-a:text-blue-600 hover:prose-a:text-blue-800">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </article>
      </div>

      {/* Footer */}
      <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500 text-center">
          Generated by Echonour • {new Date().toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}

// Helper to detect if a response should be shown as an artifact
export function shouldShowAsArtifact(userMessage: string, response: string): boolean {
  const userLower = userMessage.toLowerCase();
  
  // Explicit triggers
  const artifactTriggers = [
    'write a report',
    'create a report',
    'generate a report',
    'write an itinerary',
    'create an itinerary',
    'generate an itinerary',
    'write a summary',
    'create a document',
    'write a document',
    'draft a',
    'prepare a',
    'make a plan',
    'create a plan',
    'full itinerary',
    'detailed plan',
    'comprehensive guide',
    'write up',
    'writeup',
  ];
  
  const hasExplicitTrigger = artifactTriggers.some(t => userLower.includes(t));
  
  // Response characteristics that suggest it's a document
  const hasMultipleHeaders = (response.match(/^#{1,3}\s/gm) || []).length >= 3;
  const isLongResponse = response.length > 2000;
  const hasStructuredContent = response.includes('---') || response.includes('|') || hasMultipleHeaders;
  
  return hasExplicitTrigger || (isLongResponse && hasStructuredContent);
}

// Extract a title from the response
export function extractArtifactTitle(response: string): string {
  // Try to find first H1 or H2
  const h1Match = response.match(/^#\s+(.+)$/m);
  if (h1Match) return h1Match[1].slice(0, 50);
  
  const h2Match = response.match(/^##\s+(.+)$/m);
  if (h2Match) return h2Match[1].slice(0, 50);
  
  // Try first bold text
  const boldMatch = response.match(/\*\*([^*]+)\*\*/);
  if (boldMatch) return boldMatch[1].slice(0, 50);
  
  return "Report";
}
